import React, { useState, useCallback, useRef, useMemo } from 'react';
import { Globe } from './ui/globe';
import ShaderBackground from './ShaderBackground';
import { useTheme } from '../hooks/useTheme';

interface GlobeDialButtonProps {
  status: 'idle' | 'dialing' | 'listening' | 'processing' | 'speaking';
  isCallActive: boolean;
  onDial: () => void;
  onHangup: () => void;
  disabled?: boolean;
  analyser?: AnalyserNode | null;
}

const CONTAINER_SIZE = 200;
const PROXIMITY_RADIUS = CONTAINER_SIZE / 2;

const THEME_COLORS = {
  dark: {
    dotColor: 'rgba(255, 0, 94, ALPHA)',
    arcColor: 'rgba(255, 0, 94, 0.5)',
    markerColor: 'rgba(255, 50, 120, 1)',
    glowColor: '255, 0, 94',
  },
  light: {
    dotColor: 'rgba(224, 0, 90, ALPHA)',
    arcColor: 'rgba(224, 0, 90, 0.4)',
    markerColor: 'rgba(224, 30, 100, 1)',
    glowColor: '224, 0, 90',
  },
};

export function GlobeDialButton({
  status,
  isCallActive,
  onDial,
  onHangup,
  disabled = false,
  analyser,
}: GlobeDialButtonProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [proximity, setProximity] = useState(0);
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const colors = isDark ? THEME_COLORS.dark : THEME_COLORS.light;

  const isTouchOnly = useMemo(() => {
    if (typeof window === 'undefined') return false;
    const coarse = window.matchMedia('(pointer: coarse)').matches;
    const noHover = window.matchMedia('(hover: none)').matches;
    const noFine = !window.matchMedia('(any-pointer: fine)').matches;
    return coarse && noHover && noFine;
  }, []);

  const isIdle = !isCallActive && status !== 'dialing';
  const isDialing = status === 'dialing';

  // Touch-only devices always show the button; dialing forces closed; otherwise proximity-driven
  const revealAmount = isTouchOnly ? (isDialing ? 0 : 1) : (isDialing ? 0 : proximity);

  const clickable = revealAmount > 0.5 && !disabled;

  const handleClick = useCallback(() => {
    if (disabled) return;
    if (isCallActive) {
      onHangup();
    } else {
      onDial();
    }
  }, [disabled, isCallActive, onDial, onHangup]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dist = Math.hypot(e.clientX - cx, e.clientY - cy);
    const t = Math.max(0, Math.min(1, 1 - dist / PROXIMITY_RADIUS));
    setProximity(t);
  }, []);

  const handleMouseLeave = useCallback(() => setProximity(0), []);

  const bgOpacity = isTouchOnly ? (isDialing ? 0 : 1) : (isDialing ? 0 : proximity);

  const iconBgStyle: React.CSSProperties = isCallActive
    ? { background: `linear-gradient(135deg, rgba(239,68,68,${bgOpacity}), rgba(220,50,50,${bgOpacity}))`, color: bgOpacity > 0.5 ? 'white' : 'var(--color-danger)', boxShadow: `0 6px 20px rgba(239, 68, 68, ${0.4 * bgOpacity})` }
    : { background: `linear-gradient(135deg, rgba(255,0,94,${bgOpacity}), rgba(200,0,80,${bgOpacity}))`, color: bgOpacity > 0.5 ? 'white' : 'var(--primary-color)', boxShadow: `0 6px 20px rgba(255, 0, 94, ${0.4 * bgOpacity})` };

  return (
    <div className="globe-dial-wrapper">
      {/* Shader waveform behind the globe, extends 100px each side */}
      <ShaderBackground
        active={status === 'speaking'}
        height={CONTAINER_SIZE}
        analyser={analyser}
        isDark={isDark}
        className="globe-shader-bg"
      />

      <div
        ref={containerRef}
        className={`globe-dial-container ${isDialing ? 'dialing' : ''}`}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        {/* Opaque circle mask: hides wave lines behind the globe */}
        <div className="globe-dial-mask" />

        <div className="globe-dial-icon" style={iconBgStyle}>
          {!isCallActive && (
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M20.01 15.38c-1.23 0-2.42-.2-3.53-.56-.35-.12-.74-.03-1.01.24l-1.57 1.97c-2.83-1.35-5.48-3.9-6.89-6.83l1.95-1.66c.27-.28.35-.67.24-1.02-.37-1.11-.56-2.3-.56-3.53 0-.54-.45-.99-.99-.99H4.19C3.65 3 3 3.24 3 3.99 3 13.28 10.73 21 20.01 21c.71 0 .99-.63.99-1.18v-3.45c0-.54-.45-.99-.99-.99z"/>
            </svg>
          )}
          {isCallActive && (
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 9c-1.6 0-3.15.25-4.6.72v3.1c0 .39-.23.74-.56.9-.98.49-1.87 1.12-2.66 1.85-.18.18-.43.28-.7.28-.28 0-.53-.11-.71-.29L.29 13.08c-.18-.17-.29-.42-.29-.7 0-.28.11-.53.29-.71C3.34 8.78 7.46 7 12 7s8.66 1.78 11.71 4.67c.18.18.29.43.29.71 0 .28-.11.53-.29.71l-2.48 2.48c-.18.18-.43.29-.71.29-.27 0-.52-.11-.7-.28-.79-.74-1.69-1.36-2.67-1.85-.33-.16-.56-.5-.56-.9v-3.1C15.15 9.25 13.6 9 12 9z"/>
            </svg>
          )}
        </div>

        <Globe
          size={CONTAINER_SIZE}
          revealAmount={revealAmount}
          pulseGlow={isDialing}
          autoRotateSpeed={isDialing ? 0.006 : 0.002}
          dotColor={colors.dotColor}
          arcColor={colors.arcColor}
          markerColor={colors.markerColor}
          glowColor={colors.glowColor}
        />

        <span className="globe-dial-label">
          {isIdle && 'Dial'}
          {isDialing && 'Connecting...'}
          {isCallActive && 'Hang Up'}
        </span>

        <button
          className={`globe-dial-hitarea ${clickable ? 'active' : ''}`}
          onClick={handleClick}
          disabled={!clickable}
          aria-label={isCallActive ? 'Hang up' : 'Dial'}
        />

        {(isDialing || isCallActive) && (
          <>
            <div className="globe-pulse-ring"></div>
            <div className="globe-pulse-ring delay-1"></div>
            <div className="globe-pulse-ring delay-2"></div>
          </>
        )}
      </div>
    </div>
  );
}

export default GlobeDialButton;
